import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { DaysOffSortFields, SortDirection, GetDaysOffsQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/daysOff';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useDaysOffsTable = ({ queryOptions }: { queryOptions?: GetDaysOffsQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetDaysOffsQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as DaysOffSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withUser: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: daysOffData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetDaysOffsQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      daysOffData
        ? { totalCount: daysOffData.daysOffs.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [daysOffData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type DaysOffsBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function DaysOffsBaseTable(props: DaysOffsBaseTableProps) {
  const tableProps = useDaysOffsTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
